import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { MaterialIcon } from "../shared/MaterialIcon";

const DropdownContext = createContext(null);

const useDropdown = () => {
    const ctx = useContext(DropdownContext);
    if(!ctx) return;
    
    return ctx;
}

export const DropdownMenu = ({
    label = null,
    children,
    defaultIcon = null,
    defaultName = null,
    defaultValue = null,
    onSelect,
    defaultSelected = null,
    disabled
}) => {

    const [selected, setSelected] = useState({icon: defaultIcon, name: defaultName, value: defaultValue});
    const [isOpen, setIsOpen] = useState(false);
    

    const select = ({icon, name, value}) => {
        if(disabled) return;

        setSelected({
            icon: icon && value ? icon : defaultIcon,
            name: name && value ? name : defaultName,
            value: value ? value : defaultValue
        });

        onSelect?.(value ? value : null);
        setIsOpen(false);
    }

    const findChildByValue = useCallback((nodes, value) => {
        for (const node of nodes) {
            if (!node) continue;

            if (Array.isArray(node)) {
                const found = findChildByValue(node, value);
                if (found) return found;
            } else if (
                node.props &&
                node.props.value &&
                node.props.value === value
            ) {
                return node;
            }
        }
        return null;
    }, []);

    useEffect(() => {
        if(!children) return;
        
        if(!Array.isArray(children))
        {
            if(children.props && children.props.value && children.props.value === defaultSelected)
            {
                setSelected({
                    icon: children.props.icon ? children.props.icon : defaultIcon,
                    name: children.props.name ? children.props.name : defaultName,
                    value: children.props.value
                })
            }
        }
        else if(Array.isArray(children))
        {   
            const selectedChild = findChildByValue(children, defaultSelected);

            if(selectedChild)
            {
                setSelected({
                    icon: selectedChild.props.icon ? selectedChild.props.icon : defaultIcon,
                    name: selectedChild.props.name ? selectedChild.props.name : defaultName,
                    value: selectedChild.props.value
                })
            }
        }
        
    }, [defaultSelected, defaultIcon, defaultName, defaultValue, children, findChildByValue]);

    const dropdownRef = useRef(null);

    useEffect(() => {
        const el = dropdownRef.current;
        if(!el) return;

        const clickHandler = (e) => {

            if(!e.target) return;

            if(!el.contains(e.target)) setIsOpen(false);
        }

        document.addEventListener("click", clickHandler);

        return () => document.removeEventListener("click", clickHandler);
    })

    const dropdownContentRef = useRef(null);

    useEffect(() => {
        const el = dropdownContentRef.current
        if(!el) return;

        if(isOpen) 
        {
            const fullHeight = el.scrollHeight;
            el.style.height = "0px";
            el.style.opacity = "0";

            requestAnimationFrame(() => {
                el.style.height = `${fullHeight}px`;
                el.style.opacity = "1";
            });

            const timeout = setTimeout(() => {
                el.style.height = "auto";
            }, 110);

            return () => clearTimeout(timeout);
        }
        else if(!isOpen)
        {
            const fullHeight = el.scrollHeight;

            el.style.height = `${fullHeight}px`;
            el.style.opacity = "1";

            requestAnimationFrame(() => {
                el.style.height = "0px";
                el.style.opacity = "0";
            });
        }

    }, [isOpen, disabled])


    return (
        <DropdownContext.Provider value={{selected, isOpen, setIsOpen, select}}>
            <StyledDropdownMenu ref={dropdownRef} className={disabled ? 'dropdownDisabled' : null}>
                {label ? <div className="dropdownMenuLabel"> {label} </div> : null}
                <div className="dropdownMenuContainer">
                    <div className="dropdownMenuHeader" onClick={() => (disabled ? setIsOpen(false) : setIsOpen(!isOpen)) }>
                        <div className="headerIcon">
                            {disabled ? defaultIcon : selected.icon}
                        </div>
                        <div className="headerValue">
                            {disabled ? defaultName : selected.name}
                        </div>
                        <div className={isOpen ? "dropdownIcon dropdownIconExpandec" : "dropdownIcon"}>
                            <MaterialIcon
                                name='arrow_drop_down'
                                weight={300}
                            />
                        </div>       
                    </div>
                    <div ref={dropdownContentRef} className={isOpen ? "dropdownMenuContent isOpen" : "dropdownMenuContent"}>
                        {children}
                    </div>
                </div>
            
            </StyledDropdownMenu>
        </DropdownContext.Provider>
    )
}

export const DropdownOption = ({icon = null, name, value = null}) => {
    
    const { selected, select } = useDropdown();

    return (
        <StyledDropdownMenuOption
            onClick={() => {select({icon, name, value})}}
            className={value === selected.value ? 'isSelected' : null}
        >
            <div className="optionIcon">
                {icon}
            </div>
            <div className="optionName">
                {name}
            </div>
        </StyledDropdownMenuOption>
    )
}

const StyledDropdownMenu = styled.div`
    
    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacing.sm};

    div.dropdownMenuLabel
    {
        font-size: ${({theme}) => theme.fontSize.md};
        font-weight: 500;
        color: ${({theme}) => theme.colors.neutralPrimaryForeground};
        font-size: ${({theme}) => theme.fontSize.sm};
        padding-left: ${({theme}) => theme.spacing.md};
    }

    div.dropdownMenuContainer
    {
        position: relative;
        height: 32px;
        cursor: pointer;
        user-select: none;

        background: ${({theme}) => theme.colors.neutralInputBackground};
        border: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        border-radius: ${({theme}) => theme.cornerRadius.md};

        div.dropdownMenuHeader 
        {
            display: flex;
            align-items: center;
            gap: ${({theme}) => theme.spacing.sm};
            height: 32px;

            div.headerIcon
            {
                min-width: 50px;
                display: flex;
                align-items: center;
                justify-content: center;

                .icon
                {
                    color: ${({theme}) => theme.colors.mutedForeground};
                }
            }

            div.headerValue
            {
                flex: 1 1 auto;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                font-weight: 500;
            }

            div.dropdownIcon
            {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 30px;
                transition-duration: 220ms;

                .icon
                {
                    color: ${({theme}) => theme.colors.mutedForeground};
                }
            }

            div.dropdownIcon.dropdownIconExpandec
            {
                transform: scaleY(-1);
            }
        }

        div.dropdownMenuContent
        {
            background: ${({theme}) => theme.colors.neutralInputBackground};
            border: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
            border-radius: ${({theme}) => theme.cornerRadius.md};
            display: flex;
            flex-direction: column;
            overflow: hidden;
            height: 0;
            opacity: 0;
            transition: height 110ms ease, opacity 110ms ease;
            position: relative;
            z-index: 1000;
        }
    }

    &.dropdownDisabled
    {
        color: ${({theme}) => theme.colors.mutedForeground};
        

        > div.dropdownMenuContainer
        {
            background: ${({theme}) => theme.colors.mutedBackground};
            cursor: default;
        }
    }
`

const StyledDropdownMenuOption = styled.div`

    
    display: flex;
    align-items: center;
    gap: ${({theme}) => theme.spacing.sm};
    padding-right: 40px;
    padding-top: ${({theme}) => theme.spacing.sm};
    padding-bottom: ${({theme}) => theme.spacing.sm};

    &.isSelected
    {
        background: ${({theme}) => theme.colors.neutralSecondaryBackground};
    }

    div.optionName
    {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    div.optionIcon
    {
        width: 50px;
        flex: 0 0 50px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: ${({theme}) => theme.colors.mutedForeground};
        
    }

    &:hover
    {
        background: ${({theme}) => theme.colors.neutralSecondaryBackground};
    }
`